import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface EligibilityItem {
  readonly eligibilityId: string;
  /** The job, dealer and date the bill is known by. */
  readonly bill: BillRef | null;
  readonly vendorName: string | null;
  readonly vendorCode: string | null;
  readonly eligibleAmount: number;
  readonly eligibleTaxAmount: number;
  readonly eligibleTotal: number;
  readonly eligibilityStatus: string;
  readonly createdAt: string;
}

export function PaymentEligibilityDashboardPage(): ReactNode {
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay, day } = useBusinessDay();
  const { token } = useAuth();
  const [items, setItems] = useState<EligibilityItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<EligibilityItem[]>('/v1/approval/eligibility/pending', { token })
      .then(d => { if (alive) setItems(Array.isArray(d) ? d : []); })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const totalEligible = items.reduce((s, i) => s + Number(i.eligibleTotal ?? 0), 0);

  return (
    <div style={{ padding: 24 }}>
      <h1>{t('fa.eligibility.title')}</h1>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <div style={{ padding: 16, border: '1px solid var(--color-border-default)', borderRadius: 8, marginBottom: 16, background: 'var(--color-success-bg)' }}>
        <div style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>{t('fa.eligibility.totalEligibleAmount')}</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-success)' }}>₹{money(totalEligible)}</div>
        <div style={{ fontSize: 13 }}>{t('fa.eligibility.claimsEligible', { count: String(items.length) })}</div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{[t('fa.eligibility.colClaim'), t('fa.eligibility.colVendor'), t('fa.eligibility.colAmount'), t('fa.eligibility.colTax'), t('fa.eligibility.colTotal'), t('fa.eligibility.colStatus'), t('fa.eligibility.colCreated')].map(h => <th key={h} style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid var(--color-border-default)' }}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {items.map(e => (
            <tr key={e.eligibilityId}>
              <td style={{ padding: 8 }}>{billRefLabel(e.bill, day)}</td>
              <td style={{ padding: 8 }}>{personLabel(e.vendorName, e.vendorCode) ?? '—'}</td>
              <td style={{ padding: 8 }}>₹{money(Number(e.eligibleAmount))}</td>
              <td style={{ padding: 8 }}>₹{money(Number(e.eligibleTaxAmount))}</td>
              <td style={{ padding: 8, fontWeight: 700 }}>₹{money(Number(e.eligibleTotal))}</td>
              <td style={{ padding: 8 }}><span style={{ background: 'var(--color-success-bg)', color: 'var(--color-success)', padding: '2px 8px', borderRadius: 12 }}>{e.eligibilityStatus}</span></td>
              <td style={{ padding: 8 }}>{atDay(e.createdAt)}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('fa.eligibility.empty')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
